import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { checkPlayInvite, linkPlayAccount, type BrowserProfile } from '@/api/browserProfiles';
import { SessionShot } from './BrowserProfileEditor';

// A Play Console that belongs to somebody else, shared with this profile's
// Google account: what to ask the owner for, "Check invite" (the runner accepts
// the invitation and lists the developer accounts the login opens), and Link,
// which turns one of them into a First publication account that signs in with
// this profile. Its apps then get their Firebase project here too.

interface Props {
  profile: BrowserProfile | null;
  canLink: boolean;
  onClose: () => void;
}

const inputCls = 'w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm';

function when(ts: number): string {
  return new Date(ts * 1000).toLocaleString();
}

export function PlayConsoleShare({ profile, canLink, onClose }: Props) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showShot, setShowShot] = useState(false);
  const [form, setForm] = useState({ developer_id: '', name: '', contact_email: '' });
  const devs = profile?.play_link?.developers ?? [];

  useEffect(() => {
    setError(null);
    setShowShot(false);
    setForm((f) => ({ ...f, developer_id: devs.length === 1 ? devs[0]!.id : '', name: devs.length === 1 ? devs[0]!.name ?? '' : '' }));
    // Re-seeded when another profile opens or a fresh check lands.
  }, [profile?.id, profile?.play_link?.checked_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => qc.invalidateQueries({ queryKey: ['browser-profiles'] });
  const checkMut = useMutation({
    mutationFn: () => checkPlayInvite(profile!.id),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Could not queue the check'),
  });
  const linkMut = useMutation({
    mutationFn: () => linkPlayAccount(profile!.id, {
      developer_id: form.developer_id, name: form.name.trim() || undefined, contact_email: form.contact_email.trim() || undefined,
    }),
    onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: ['play-accounts'] }); },
    onError: (e: any) => setError(e?.message ?? 'Could not link the account'),
  });

  if (!profile) return null;
  const busy = !!profile.play_link_requested_at || !!profile.play_link_started_at;
  const copy = async () => {
    if (!profile.google_email) return;
    await navigator.clipboard.writeText(profile.google_email).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Modal isOpen={!!profile} onClose={onClose} title={`Play Console · ${profile.name}`}>
      <div className="flex flex-col gap-4 text-sm">
        {error && <div className="px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-400">{error}</div>}

        {profile.play_account_id ? (
          <div className="px-3 py-2 rounded-md bg-green-500/10 border border-green-500/30 text-green-400">
            Publishes to “{profile.play_account_name}”. Set up its service account and run publications in{' '}
            <Link to="/play/publish" className="underline">First publication → Console access</Link>.
            This profile now makes Firebase projects only for that console’s apps.
          </div>
        ) : (
          <section>
            <h3 className="font-medium text-content mb-1">1. Ask the console’s owner for access</h3>
            <p className="text-content-muted mb-2">
              In Play Console: <i>Users and permissions → Invite new users</i>, this e-mail, <i>All apps</i>, and every
              account permission except Admin, users and finance — we create apps, fill their store listing and policy
              declarations, and release to testing and production.
            </p>
            <div className="flex items-center gap-2">
              <code className="px-2 py-1 rounded bg-surface-muted text-content">{profile.google_email ?? 'no Google account saved'}</code>
              {profile.google_email && <Button size="sm" variant="secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>}
            </div>
            <p className="text-content-muted mt-2">
              Later the owner also invites our service account (First publication shows its address once it exists) —
              our login there is no Admin, so it cannot invite anybody itself.
            </p>
          </section>
        )}

        <section>
          <h3 className="font-medium text-content mb-1">{profile.play_account_id ? 'Check invite' : '2. Check invite'}</h3>
          <p className="text-content-muted mb-2">
            The runner opens this profile, accepts the invitation (in Play Console, or from the mail in this account’s
            Gmail) and lists the developer accounts the login opens. If it cannot, open the profile in the live browser,
            accept the invitation by hand and check again.
          </p>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="secondary" disabled={!profile.shardx_profile_id || busy} loading={checkMut.isPending}
              onClick={() => { setError(null); checkMut.mutate(); }}>
              Check invite
            </Button>
            <span className="text-xs text-content-muted">
              {profile.play_link_started_at ? 'the runner is on it…'
                : profile.play_link_requested_at ? 'queued'
                  : profile.play_link ? `checked ${when(profile.play_link.checked_at)}${profile.play_link.accepted ? ' · invitation accepted' : ''}` : 'never checked'}
            </span>
            {profile.has_session_evidence && (
              <button className="text-xs text-accent hover:underline" onClick={() => setShowShot((v) => !v)}>
                {showShot ? 'Hide screenshot' : 'Screenshot'}
              </button>
            )}
          </div>
          {profile.play_link_error && <p className="mt-2 text-xs text-red-400">{profile.play_link_error}</p>}
          {showShot && <div className="mt-2"><SessionShot profileId={profile.id} /></div>}
          {profile.play_link && !busy && (
            devs.length
              ? <p className="mt-2 text-content-muted">Opens: {devs.map((d) => `${d.name ?? 'unnamed'} (${d.id})`).join(', ')}</p>
              : <p className="mt-2 text-amber-400">No developer account found yet — the invitation is not accepted.</p>
          )}
        </section>

        {!profile.play_account_id && devs.length > 0 && (
          <section>
            <h3 className="font-medium text-content mb-1">3. Link it to First publication</h3>
            {!canLink ? (
              <p className="text-content-muted">An administrator or developer links the account.</p>
            ) : (
              <div className="flex flex-col gap-2">
                <select className={inputCls} value={form.developer_id}
                  onChange={(e) => setForm({ ...form, developer_id: e.target.value, name: devs.find((d) => d.id === e.target.value)?.name ?? form.name })}>
                  <option value="">Developer account…</option>
                  {devs.map((d) => <option key={d.id} value={d.id}>{d.name ?? 'unnamed'} · {d.id}</option>)}
                </select>
                <input className={inputCls} placeholder="Account name (defaults to the developer name)" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input className={inputCls} placeholder="Public contact e-mail for the store (can be set later)" value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                <div className="flex justify-end">
                  <Button size="sm" disabled={!form.developer_id} loading={linkMut.isPending}
                    onClick={() => { setError(null); linkMut.mutate(); }}>
                    Link
                  </Button>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </Modal>
  );
}
